import { useParams, Link } from 'react-router-dom';
import { comics } from '@/mocks/comics';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { useState, useEffect } from 'react';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));
  const [showBackTop, setShowBackTop] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-secondary-100 flex items-center justify-center">
              <div className="w-10 h-10 flex items-center justify-center">
                <i className="ri-file-unknow-line text-3xl text-foreground-300"></i>
              </div>
            </div>
            <h1 className="text-xl font-heading text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">抱歉，该漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-primary-500 text-background-50 dark:text-foreground-950 px-5 py-2 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const chapters = Array.from({ length: Math.min(comic.chapters, 50) }, (_, i) => {
    const chNum = comic.chapters - i;
    return {
      id: chNum,
      title: `第${chNum}话`,
      date: new Date(Date.now() - i * 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    };
  });

  const relatedComics = comics.filter((c) =>
    c.id !== comic.id && c.categories.some((cat) => comic.categories.includes(cat))
  ).slice(0, 6);

  const ldJson = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 飞飞漫画网免费漫画`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2024-01-01',
    dateModified: comic.updatedAt,
    image: comic.cover,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': window.location.href,
    },
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        <script type="application/ld+json">{JSON.stringify(ldJson)}</script>

        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '分类浏览', href: `/category/${comic.categories[0]}` },
            { label: comic.title },
          ]}
        />

        <section className="py-6 md:py-10">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              <div className="flex-shrink-0 w-full md:w-[260px]">
                <div className="w-[200px] h-[266px] mx-auto md:mx-0 rounded-lg overflow-hidden">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover"
                    title={`${comic.title} - 飞飞漫画网`}
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-2">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-4">
                  作者：<strong>{comic.author}</strong>
                </p>

                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                    comic.status === '连载中'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                    {comic.rating} 分
                  </span>
                  <span className="text-xs bg-background-200 text-foreground-600 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                    {comic.views} 次阅读
                  </span>
                  <span className="text-xs bg-background-200 text-foreground-600 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                    共 {comic.chapters} 话
                  </span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="text-xs text-foreground-500 bg-background-100 border border-background-200/70 px-2.5 py-1 rounded-md whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6 max-w-2xl">
                  {comic.description}
                </p>

                <div className="flex items-center gap-3 flex-wrap">
                  <Link
                    to={`/comic/${comic.id}`}
                    className="inline-flex items-center gap-2 bg-primary-500 text-background-50 dark:text-foreground-950 px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    开始阅读
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-book-open-line"></i>
                    </div>
                  </Link>
                  <button className="inline-flex items-center gap-2 bg-secondary-100 text-foreground-700 px-6 py-2.5 rounded-full text-sm font-medium hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer">
                    收藏漫画
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-heart-line"></i>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-8 md:py-12 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-lg font-heading text-foreground-950 mb-4">章节列表</h2>
            <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
              <div className="max-h-[500px] overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-background-200/70">
                  {chapters.map((ch) => (
                    <Link
                      key={ch.id}
                      to={`/comic/${comic.id}`}
                      className="flex items-center justify-between px-4 py-3 bg-background-50 hover:bg-primary-50 transition-colors cursor-pointer"
                    >
                      <span className="text-sm text-foreground-700 truncate">{ch.title}</span>
                      <span className="text-xs text-foreground-400 whitespace-nowrap ml-2">{ch.date}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            <p className="text-xs text-foreground-400 mt-3 text-center">
              显示最近 50 话，共 {comic.chapters} 话
            </p>
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="py-10 md:py-14">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-lg font-heading text-foreground-950 mb-4">相关推荐</h2>
              <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-2">
                {relatedComics.map((c) => (
                  <ComicCard
                    key={c.id}
                    id={c.id}
                    title={c.title}
                    author={c.author}
                    cover={c.cover}
                    category={c.category}
                    categories={c.categories}
                    rating={c.rating}
                    views={c.views}
                    status={c.status}
                    size="md"
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`fixed bottom-8 right-8 z-40 w-10 h-10 rounded-full bg-primary-500 text-background-50 flex items-center justify-center shadow-lg hover:bg-primary-600 transition-all duration-300 cursor-pointer ${
          showBackTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
        aria-label="回到顶部"
      >
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-arrow-up-line text-lg"></i>
        </div>
      </button>
    </>
  );
}